import type { Ref, ComputedRef } from 'vue'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import dayjs from 'dayjs' //FIXME: days => useDayjs

import { useTutorKanbanStore } from './store'
import { useTutorKanbanStage } from './tutor-kanban-stage.hook'

interface TutorKanbanPickerDeadlineReturn {
  refDate: Ref<string | null>
  refStartTime: Ref<{ hours: number, minutes: number }>
  refMinDate: ComputedRef<string>
  refExpired: ComputedRef<boolean>
  refUpcoming: ComputedRef<boolean>
  refStageAge: ComputedRef<string | null>
  handleChange: (date: string | null) => void
}

export const useTutorKanbanPickerDeadline = (): TutorKanbanPickerDeadlineReturn => {
  const tutorKanbanStore = useTutorKanbanStore()
  const { isExpired, isUpcoming } = useTutorKanbanStage()

  const getInitialValue = (): string | null => {
    const deadline = tutorKanbanStore.getTutor?.stage_deadline ?? null
    return deadline ? dayjs(deadline, DateFormat.Default).format() : null
  }

  const refDate = ref<string | null>(getInitialValue())
  const refStartTime = ref({ hours: 14, minutes: 0 })

  watch(
    () => tutorKanbanStore.getTutor?.stage_deadline ?? null,
    (deadline) => {
      refDate.value = deadline ? dayjs(deadline, DateFormat.Default).format() : null
    },
  )

  const refMinDate = computed<string>(() => {
    return dayjs().startOf('day').format(DateFormat.Default)
  })

  const refExpired = computed<boolean>(() => {
    return isExpired(tutorKanbanStore.getTutor?.stage_deadline ?? null)
  })

  const refUpcoming = computed<boolean>(() => {
    return isUpcoming(tutorKanbanStore.getTutor?.stage_deadline ?? null)
  })

  const refStageAge = computed<string | null>(() => {
    const updatedAt = tutorKanbanStore.getTutor?.stage_updated_at ?? null
    if (!updatedAt) return null
    const days = dayjs().diff(dayjs(updatedAt, DateFormat.Default), 'day')
    if (days <= 0) return 'Today'
    return `${days} day${days === 1 ? '' : 's'}`
  })

  const handleChange = (date: string | null): void => {
    const value = (!!date && date !== 'Invalid Date')
      ? dayjs(date).format(DateFormat.Default)
      : null

    tutorKanbanStore.setTutorStageDeadline(value)
  }

  return {
    refDate,
    refStartTime,
    refMinDate,
    refExpired,
    refUpcoming,
    refStageAge,
    handleChange,
  }
}
